<template>
  <div class="flow-chart-container">
    <div ref="graphContainer" class="graph-container"></div>
  </div>
</template>

<script>
import { Graph } from '@antv/x6'

// 常量配置
const GRAPH_CONFIG = {
  width: 226,
  height: 600,
  nodeWidth: 140,
  nodeHeight: 50,
  nodeSpacing: 85,
  startY: 40
}

const GRADIENT_FILL = {
  type: 'linearGradient',
  stops: [
    { offset: '0%', color: '#F5F6FF' },
    { offset: '100%', color: '#FFFFFF' }
  ]
}

// 节点状态颜色配置
const STATUS_COLORS = {
  pending: {
    stroke: '#E5E7EB',
    textColor: '#000000',
    showCheck: false
  },
  running: {
    stroke: '#818CF8',
    textColor: '#000000',
    showCheck: false
  },
  completed: {
    stroke: '#4D53E8',
    textColor: '#000000',
    showCheck: true
  }
}

export default {
  name: 'FlowChart',

  props: {
    isRunning: {
      type: Boolean,
      default: false
    },
    nodes: {
      type: Array,
      default: () => []
    },
    edges: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      graph: null,
      animationFrames: []
    }
  },

  mounted () {
    this.$nextTick(() => {
      this.initGraph()
      this.renderFlow()
    })
  },

  beforeDestroy () {
    this.clearAnimations()
    if (this.graph) {
      this.graph.dispose()
    }
  },

  watch: {
    nodes: {
      handler () {
        this.renderFlow()
        this.$nextTick(() => {
          setTimeout(() => this.addFlowingParticles(), 100)
        })
      },
      deep: true
    },
    isRunning () {
      this.$nextTick(() => this.addFlowingParticles())
    }
  },

  methods: {
    // 初始化画布
    initGraph () {
      this.graph = new Graph({
        container: this.$refs.graphContainer,
        width: GRAPH_CONFIG.width,
        height: GRAPH_CONFIG.height,
        background: { color: 'transparent' },
        grid: false,
        interacting: false,
        panning: false,
        mousewheel: false
      })

      this.registerCustomNodes()
      this.registerCustomEdges()
    },

    // 注册自定义节点
    registerCustomNodes () {
      // 基础节点配置
      const baseNodeConfig = {
        inherit: 'rect',
        width: GRAPH_CONFIG.nodeWidth,
        height: GRAPH_CONFIG.nodeHeight,
        attrs: {
          body: {
            strokeWidth: 1,
            stroke: '#E5E7EB',
            rx: 6,
            ry: 6,
            fill: GRADIENT_FILL
          },
          label: {
            fontSize: 14,
            fill: '#000000',
            fontWeight: 500,
            textAnchor: 'middle',
            textVerticalAnchor: 'middle'
          }
        }
      }

      // 注册开始节点
      Graph.registerNode('flow-start', baseNodeConfig, true)

      // 注册流程节点（带对勾标记）
      Graph.registerNode('flow-process', {
        ...baseNodeConfig,
        markup: [
          { tagName: 'rect', selector: 'body' },
          { tagName: 'text', selector: 'label' },
          { tagName: 'circle', selector: 'checkBg' },
          { tagName: 'path', selector: 'checkmark' }
        ],
        attrs: {
          ...baseNodeConfig.attrs,
          checkBg: {
            r: 8,
            cx: 125,
            cy: 40,
            fill: '#52C41A',
            stroke: '#FFFFFF',
            strokeWidth: 1.5,
            display: 'none'
          },
          checkmark: {
            d: 'M 120 40 L 123 43 L 130 36',
            stroke: '#FFFFFF',
            strokeWidth: 2,
            fill: 'none',
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            display: 'none'
          }
        }
      }, true)

      // 注册结束节点
      Graph.registerNode('flow-end', baseNodeConfig, true)
    },

    // 注册自定义边
    registerCustomEdges () {
      const edgeConfig = (stroke) => ({
        inherit: 'edge',
        attrs: {
          line: {
            stroke,
            strokeWidth: 2,
            targetMarker: {
              name: 'classic',
              size: 7,
              fill: stroke
            }
          }
        }
      })

      Graph.registerEdge('flow-edge', edgeConfig('#E5E7EB'), true)
      Graph.registerEdge('flow-edge-active', edgeConfig('#4D53E8'), true)
    },

    // 渲染流程图
    renderFlow () {
      if (!this.graph || !this.nodes.length) return

      this.graph.clearCells()
      this.renderNodes()
      this.renderEdges()

      this.$nextTick(() => {
        setTimeout(() => this.addFlowingParticles(), 100)
      })
    },

    // 渲染节点
    renderNodes () {
      this.nodes.forEach((node, index) => {
        const nodeConfig = {
          id: node.id,
          shape: this.getNodeShape(node.type),
          x: (GRAPH_CONFIG.width - GRAPH_CONFIG.nodeWidth) / 2,
          y: GRAPH_CONFIG.startY + index * GRAPH_CONFIG.nodeSpacing,
          label: node.label,
          data: { status: node.status }
        }

        this.applyNodeStatus(nodeConfig, node.status)
        this.graph.addNode(nodeConfig)
      })
    },

    // 渲染连接线
    renderEdges () {
      this.edges.forEach((edge, index) => {
        const sourceNode = this.nodes.find(n => n.id === edge.source)
        const isActive = sourceNode && 
          (sourceNode.status === 'completed' || sourceNode.status === 'running')

        this.graph.addEdge({
          id: `edge-${index}`,
          shape: isActive ? 'flow-edge-active' : 'flow-edge',
          source: edge.source,
          target: edge.target
        })
      })
    },

    // 获取节点形状类型
    getNodeShape (type) {
      const shapeMap = {
        start: 'flow-start',
        end: 'flow-end'
      }
      return shapeMap[type] || 'flow-process'
    },

    // 应用节点状态样式
    applyNodeStatus (nodeConfig, status) {
      const colorConfig = STATUS_COLORS[status] || STATUS_COLORS.pending

      nodeConfig.attrs = {
        body: {
          strokeWidth: status === 'completed' ? 2 : 1,
          stroke: colorConfig.stroke,
          rx: 6,
          ry: 6,
          fill: GRADIENT_FILL
        },
        label: {
          fontSize: 14,
          fill: colorConfig.textColor,
          fontWeight: status === 'completed' ? 600 : 500,
          textAnchor: 'middle',
          textVerticalAnchor: 'middle'
        }
      }

      // 仅流程节点显示对勾
      if (nodeConfig.shape === 'flow-process') {
        const display = colorConfig.showCheck ? 'block' : 'none'
        nodeConfig.attrs.checkBg = { display }
        nodeConfig.attrs.checkmark = { display }
      }
    },

    // 添加流动光点动画
    addFlowingParticles () {
      this.clearAnimations()

      const runningNode = this.nodes.find(node => node.status === 'running')
      if (!runningNode) return

      const edges = this.graph.getEdges()
      const outgoingEdge = edges.find(edge => edge.getSourceCellId() === runningNode.id)

      if (outgoingEdge) {
        this.animateParticle(outgoingEdge)
      }
    },

    // 光点动画
    animateParticle (edge) {
      const edgeView = this.graph.findViewByCell(edge)
      if (!edgeView) return

      const pathElement = this.getPathElement(edgeView)
      if (!pathElement) return

      const particle = this.createParticle()
      const svg = this.$refs.graphContainer.querySelector('svg')
      if (svg) svg.appendChild(particle)

      this.startParticleAnimation(particle, pathElement)
    },

    // 获取路径元素
    getPathElement (edgeView) {
      const selectors = ['path.x6-edge-path', 'path', '.x6-edge-line']
      for (const selector of selectors) {
        const element = edgeView.container.querySelector(selector)
        if (element) return element
      }
      return null
    },

    // 创建光点元素
    createParticle () {
      const particle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
      particle.setAttribute('r', '5')
      particle.setAttribute('fill', '#4D53E8')
      particle.setAttribute('class', 'flow-particle')
      particle.style.filter = 'drop-shadow(0 0 8px rgba(77, 83, 232, 0.8))'
      particle.style.opacity = '1'
      return particle
    },

    // 启动光点动画
    startParticleAnimation (particle, pathElement) {
      const pathLength = pathElement.getTotalLength()
      let progress = 0
      const speed = 0.01

      const animate = () => {
        if (!this.graph || !particle.parentNode) return

        progress = (progress + speed) % 1

        try {
          const point = pathElement.getPointAtLength(progress * pathLength)
          particle.setAttribute('cx', point.x)
          particle.setAttribute('cy', point.y)
        } catch (e) {
          return
        }

        const frameId = requestAnimationFrame(animate)
        this.animationFrames.push(frameId)
      }

      animate()
    },

    // 清除动画
    clearAnimations () {
      this.animationFrames.forEach(frameId => cancelAnimationFrame(frameId))
      this.animationFrames = []

      const svg = this.$refs.graphContainer.querySelector('svg')
      if (svg) {
        svg.querySelectorAll('.flow-particle').forEach(p => p.remove())
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import './styles/flow-chart.scss';
</style>
